// signupFunnel.ts — CORE-1277
//
// Какой CTA привёл человека к регистрации.
//
// Две из восьми целей Adventum — это одна и та же регистрация, различающаяся
// только тем, откуда всё началось: «Начать путь» на главной или «+ Live-сессия»
// в шапке. Между кликом и успехом человек проходит окно «Войти или
// зарегистрироваться», переход на другой экран и заполнение формы, то есть
// несколько раз меняет состояние React-дерева и может успеть перезагрузить
// страницу. Держать источник в состоянии компонента поэтому нельзя — он живёт в
// сторадже, ровно столько, сколько длится начатая регистрация.

import { storage } from '../utils/storage';
import { SignupSources, type SignupSource } from './marketingTags';

const KEY = 'agape_signup_funnel_v1';

/**
 * Сколько метка считается относящейся к этой регистрации.
 *
 * Час — это «человек отвлёкся, вернулся и дозаполнил форму», но не «вчера
 * посмотрел главную, а сегодня пришёл по прямой ссылке и завёл аккаунт».
 * Без срока метка пережила бы недели и приписала бы CTA чужую конверсию —
 * а цель воронки тем и ценна, что показывает, какой призыв работает.
 */
const TTL_MS = 60 * 60 * 1000;

interface StoredFunnel {
  source: SignupSource;
  markedAt: number;
}

const isKnown = (value: unknown): value is SignupSource =>
  value === SignupSources.startPath || value === SignupSources.liveSession;

/** Свежа ли метка. Вынесено отдельно, чтобы срок проверялся тестом без стораджа. */
export function isFunnelFresh(stored: StoredFunnel | null, now: number): boolean {
  if (!stored || !isKnown(stored.source)) return false;
  const markedAt = typeof stored.markedAt === 'number' ? stored.markedAt : 0;
  return now - markedAt < TTL_MS && now >= markedAt;
}

/**
 * Запомнить источник воронки.
 *
 * Ставится не на клик по CTA, а на выбор «Зарегистрироваться» в окне: человек,
 * который окно закрыл или вошёл в существующий аккаунт, регистрацию не начинал,
 * и оставленная за ним метка приписала бы этому CTA следующую — чужую —
 * регистрацию.
 */
export async function markSignupFunnel(source: SignupSource): Promise<void> {
  try {
    await storage.setJSON<StoredFunnel>(KEY, { source, markedAt: Date.now() });
  } catch {
    // Приватный режим: воронка просто останется неразмеченной. Регистрацию из-за
    // этого не ломаем — она первична, разметка производна.
  }
}

/** Источник начатой регистрации или `null`, если её никто не начинал. */
export async function readSignupFunnel(): Promise<SignupSource | null> {
  const stored = await storage.getJSON<StoredFunnel>(KEY).catch(() => null);
  return isFunnelFresh(stored, Date.now()) ? stored!.source : null;
}

/** Забыть источник: регистрация закончилась — успехом или отказом от неё. */
export async function clearSignupFunnel(): Promise<void> {
  await storage.removeJSON(KEY).catch(() => {});
}
